import { Questionnaire, QuestionnaireItem } from "@medplum/fhirtypes";

import ClientIntakeQuestionnaire from "@/fhir/seed-data/questionnaires/qh-intake-forms.json";

import { INTAKE_DEPENDENCIES } from "../constants/client-intake";
import { IntakeDependentFeature, RemovedDependentIntakeItem } from "../types/client-intake";

/** Display names for intake items whose template text is not what the editors show. */
const INTAKE_ITEM_DISPLAY_NAMES: Record<string, string> = {
  billingInformation: "Billing Information",
  contactInformationPhoneFormsetPhone: "Phone number",
  contactInformationPhoneFormsetType: "Phone type",
};

/**
 * The label the intake editors show for a section or question.
 *
 * @param linkId - the intake item's linkId.
 * @returns the display name override, else the text from the intake template, else the linkId.
 */
export const getIntakeItemLabel = (linkId: string): string =>
  INTAKE_ITEM_DISPLAY_NAMES[linkId] ?? TEMPLATE_ITEMS.get(linkId)?.text ?? linkId;

/**
 * Lists the intake content other features depend on that an intake edit removes.
 *
 * @param beforeItems - the intake items before the edit.
 * @param afterItems - the intake items after the edit.
 * @returns the removed items in `INTAKE_DEPENDENCIES` order. A removed question is left out when
 *   its whole section is removed, so the section is named once.
 */
export const getRemovedDependentIntakeItems = (
  beforeItems: QuestionnaireItem[] = [],
  afterItems: QuestionnaireItem[] = [],
): RemovedDependentIntakeItem[] => {
  const beforeLinkIds = new Set(collectLinkIds(beforeItems));
  const afterLinkIds = new Set(collectLinkIds(afterItems));

  return Object.entries(INTAKE_DEPENDENCIES).reduce<RemovedDependentIntakeItem[]>(
    (removedItems, [linkId, features]) => {
      const sectionLinkId = TEMPLATE_ITEMS.get(linkId)?.sectionLinkId ?? linkId;
      const isRemoved = beforeLinkIds.has(linkId) && !afterLinkIds.has(linkId);
      const isSectionKept = sectionLinkId === linkId || afterLinkIds.has(sectionLinkId);
      if (isRemoved && isSectionKept) {
        removedItems.push({ linkId, label: getIntakeItemLabel(linkId), features });
      }
      return removedItems;
    },
    [],
  );
};

/**
 * Lists the intake content a feature depends on that none of the given intakes collects.
 *
 * @param intakes - the intake versions to check, such as a client's own and the practice's active
 *   intake. An item counts as removed only when every one of them lacks it.
 * @param feature - the feature reading the intake data.
 * @returns labels of the removed content, in `INTAKE_DEPENDENCIES` order.
 */
export const getRemovedIntakeItemLabels = (
  intakes: Questionnaire[],
  feature: IntakeDependentFeature,
): string[] =>
  getRemovedDependentIntakeItems(
    INTAKE_TEMPLATE.item,
    intakes.flatMap((intake) => intake.item ?? []),
  ).reduce<string[]>((labels, item) => {
    if (item.features.includes(feature)) labels.push(item.label);
    return labels;
  }, []);

const collectLinkIds = (items: QuestionnaireItem[] = []): string[] =>
  items.flatMap((item) => [item.linkId, ...collectLinkIds(item.item)]);

const indexTemplateItems = (
  items: QuestionnaireItem[],
  sectionLinkId?: string,
): [string, { text?: string; sectionLinkId: string }][] =>
  items.flatMap((item) => {
    const itemSectionLinkId = sectionLinkId ?? item.linkId;
    return [
      [item.linkId, { text: item.text, sectionLinkId: itemSectionLinkId }],
      ...indexTemplateItems(item.item ?? [], itemSectionLinkId),
    ];
  });

const INTAKE_TEMPLATE = ClientIntakeQuestionnaire as Questionnaire;

const TEMPLATE_ITEMS = new Map(indexTemplateItems(INTAKE_TEMPLATE.item ?? []));
